import * as React from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Save, ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useCanWrite } from "@/components/auth/admin-auth-guard";

interface UserSubscription {
  id: string;
  plan: string;
  status: string;
  monthlyCredits: number;
  creditsUsed: number;
  maxCanvases: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
}

interface UserDetail {
  id: string;
  email: string;
  name: string;
  image?: string;
  role: string;
  emailVerified: boolean;
  banned: boolean;
  banReason?: string;
  banExpires?: string;
  twoFactorEnabled: boolean;
  referredById?: string;
  createdAt: string;
  updatedAt: string;
  subscription?: UserSubscription | null;
  _count: {
    canvases: number;
    jobs: number;
    invites: number;
    sessions: number;
    referrals: number;
  };
}

function getInitials(name: string): string {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export const Route = createFileRoute('/dashboard/users/$id')({
  component: UserDetailPage,
})

function UserDetailPage() {
  const navigate = useNavigate();
  const { id: userId } = Route.useParams();
  const canWrite = useCanWrite();

  const [user, setUser] = React.useState<UserDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);

  const [name, setName] = React.useState("");
  const [role, setRole] = React.useState("user");
  const [emailVerified, setEmailVerified] = React.useState(false);
  const [banned, setBanned] = React.useState(false);
  const [banReason, setBanReason] = React.useState("");

  const [showCancelDialog, setShowCancelDialog] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    if (!user) return;
    const changed =
      name !== user.name ||
      role !== user.role ||
      emailVerified !== user.emailVerified ||
      banned !== user.banned ||
      (banned && banReason !== (user.banReason || ""));
    setHasChanges(changed);
  }, [user, name, role, emailVerified, banned, banReason]);

  React.useEffect(() => {
    if (!userId) return;
    const fetchUser = async () => {
      try {
        const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/users/${userId}`, { credentials: "include" });
        if (!response.ok) throw new Error("Failed to fetch user");
        const result = await response.json();
        const userData = result.data.user;
        setUser(userData);
        setName(userData.name || "");
        setRole(userData.role || "user");
        setEmailVerified(userData.emailVerified || false);
        setBanned(userData.banned || false);
        setBanReason(userData.banReason || "");
        document.title = `${userData.name || userData.email} - Users - Admin`;
      } catch (error) {
        console.error("Error fetching user:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/users/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, role, emailVerified, banned, banReason: banned ? banReason : null }),
      });
      if (!response.ok) throw new Error("Failed to update user");
      const result = await response.json();
      setUser(result.data.user);
      setHasChanges(false);
      toast.success("User updated successfully!");
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Failed to update user.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    setDeleting(true);
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/users/${user.id}`, { method: "DELETE", credentials: "include" });
      if (!response.ok) throw new Error("Failed to delete user");
      toast.success("User deleted successfully.");
      navigate({ to: "/dashboard/users" });
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user.");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) { setShowCancelDialog(true); } else { navigate({ to: "/dashboard/users" }); }
  };

  if (loading) return (<div className="bg-background flex h-full items-center justify-center"><p className="text-muted-foreground">Loading user...</p></div>);
  if (!user) return (<div className="bg-background flex h-full items-center justify-center"><p className="text-muted-foreground">User not found</p></div>);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-10 shrink-0 items-center gap-2 border-b px-3 md:h-12 md:px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard" className="text-xs">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink href="/dashboard/users" className="text-xs">Users</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage className="text-xs">{user.name || user.email}</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border flex flex-col border-b px-3 py-3 md:px-4 md:py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12"><AvatarImage src={user.image || undefined} alt={user.name} /><AvatarFallback>{getInitials(user.name || user.email)}</AvatarFallback></Avatar>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-lg font-bold tracking-tight md:text-xl">{user.name}</h1>
                  <Badge variant={user.role === "admin" ? "default" : user.role === "viewer" ? "secondary" : "outline"} className="text-xs">{user.role}</Badge>
                  {user.banned && <Badge variant="destructive" className="text-xs">Banned</Badge>}
                  {!user.emailVerified && <Badge variant="outline" className="text-xs text-amber-600">Unverified</Badge>}
                  {user.twoFactorEnabled && <Badge variant="outline" className="text-xs">2FA</Badge>}
                </div>
                <p className="text-muted-foreground text-sm">{user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={saving}><ArrowLeft className="mr-1.5 h-3.5 w-3.5" />Back</Button>
              {canWrite && <Button size="sm" onClick={handleSave} disabled={!hasChanges || saving}><Save className="mr-1.5 h-3.5 w-3.5" />{saving ? "Saving..." : "Save"}</Button>}
            </div>
          </div>
        </div>
      </header>
      <main className="space-y-6 px-3 py-4 md:px-4 max-w-4xl">
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Profile</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label htmlFor="name">Name</Label><Input id="name" value={name} onChange={(e) => setName(e.target.value)} disabled={!canWrite} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={user.email} disabled className="bg-muted" /></div>
            <div className="space-y-2"><Label>User ID</Label><Input value={user.id} disabled className="bg-muted font-mono text-xs" /></div>
            <div className="space-y-2"><Label>Joined</Label><Input value={formatDate(user.createdAt)} disabled className="bg-muted" /></div>
          </div>
        </section>
        <Separator />
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Role & Permissions</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={setRole} disabled={!canWrite}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="user">User</SelectItem><SelectItem value="admin">Admin</SelectItem><SelectItem value="viewer">Viewer</SelectItem></SelectContent></Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><Label>Email Verified</Label><p className="text-muted-foreground text-xs">Manually verify the user&apos;s email</p></div>
              <Switch checked={emailVerified} onCheckedChange={setEmailVerified} disabled={!canWrite} />
            </div>
          </div>
        </section>
        <Separator />
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Ban Management</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><Label>Banned</Label><p className="text-muted-foreground text-xs">{banned ? "User is currently banned" : "User is active"}</p></div>
              <Switch checked={banned} onCheckedChange={setBanned} disabled={!canWrite} />
            </div>
            {banned && (<div className="space-y-2"><Label htmlFor="banReason">Ban Reason</Label><Textarea id="banReason" placeholder="Reason for banning this user..." value={banReason} onChange={(e) => setBanReason(e.target.value)} rows={3} /></div>)}
          </div>
        </section>
        <Separator />
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Subscription</h2>
          {user.subscription ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Plan</span><p className="font-semibold">{user.subscription.plan}</p></div>
              <div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Status</span><p className="font-semibold">{user.subscription.status}</p></div>
              <div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Credits Used</span><p className="font-semibold">{user.subscription.creditsUsed} / {user.subscription.monthlyCredits}</p></div>
              <div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Max Canvases</span><p className="font-semibold">{user.subscription.maxCanvases}</p></div>
              {user.subscription.currentPeriodEnd && (<div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Period Ends</span><p className="font-semibold">{formatDate(user.subscription.currentPeriodEnd)}</p></div>)}
              {user.subscription.cancelAtPeriodEnd && (<div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Cancel at Period End</span><p className="font-semibold text-amber-600">Yes</p></div>)}
            </div>
          ) : (<p className="text-muted-foreground text-sm">No subscription</p>)}
        </section>
        <Separator />
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Activity</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            <div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Canvases</span><p className="text-xl font-semibold">{user._count.canvases}</p></div>
            <div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Jobs</span><p className="text-xl font-semibold">{user._count.jobs}</p></div>
            <div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Invites</span><p className="text-xl font-semibold">{user._count.invites}</p></div>
            <div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Sessions</span><p className="text-xl font-semibold">{user._count.sessions}</p></div>
            <div className="rounded-lg border p-3 space-y-1"><span className="text-muted-foreground text-xs">Referrals</span><p className="text-xl font-semibold">{user._count.referrals}</p></div>
          </div>
        </section>
        <Separator />
        {canWrite && (
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-destructive">Danger Zone</h2>
          <div className="flex items-center justify-between rounded-lg border border-destructive/20 p-4">
            <div><p className="font-medium">Delete User</p><p className="text-muted-foreground text-xs">Permanently delete this user and all associated data. This action cannot be undone.</p></div>
            <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)}><Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete</Button>
          </div>
        </section>
        )}
        <div className="h-8" />
      </main>
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Discard changes?</AlertDialogTitle><AlertDialogDescription>You have unsaved changes. Are you sure you want to leave?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Stay</AlertDialogCancel><AlertDialogAction onClick={() => navigate({ to: "/dashboard/users" })}>Discard</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete user?</AlertDialogTitle><AlertDialogDescription>This will permanently delete <strong>{user.name}</strong> ({user.email}) and all associated data including canvases, jobs, and subscriptions. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{deleting ? "Deleting..." : "Delete User"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
